import type { RouteRecordRaw, Router } from 'vue-router'
import type { MenuNode } from '@/types/api'

/**
 * 菜单树 → 动态路由（附录C §2）：组件按菜单 path 约定懒加载，找不到时回落到占位页，
 * 由占位页显式提示「页面未实现」，而不是静默 404。
 */
const adminViews = import.meta.glob('/src/views/admin/**/*.vue')

function resolveView(path: string): RouteRecordRaw['component'] {
  const key = `/src/views/admin/${path.replace(/^\/+/, '')}.vue`
  const matched = adminViews[key] as (() => Promise<unknown>) | undefined
  return (matched ?? (() => import('@/views/admin/PlaceholderView.vue'))) as RouteRecordRaw['component']
}

export function menuToRoutes(nodes: MenuNode[]): RouteRecordRaw[] {
  return nodes.map((node) => {
    const path = node.path.replace(/^\/+/, '')
    const children = node.children?.length ? menuToRoutes(node.children) : undefined
    return {
      path,
      name: node.name || path,
      component: resolveView(path),
      meta: { title: node.title, icon: node.icon ?? null, permCode: node.permCode ?? null },
      ...(children ? { children } : {}),
    } as RouteRecordRaw
  })
}

/** 已动态注册的菜单路由名，切换应用时先移除，避免残留上一应用的路由。 */
let dynamicRouteNames: string[] = []

/**
 * 把菜单树注册到名为 `admin` 的父路由下（重复调用会先清理上一批）。
 *
 * 迭代0 起管理后台页面已按固定路径静态注册（便于开发与联调），
 * 因此这里既要按 name 去重，也要按**完整路径**去重，避免同一路径被注册两次。
 */
export function addMenuRoutes(router: Router, nodes: MenuNode[]): void {
  dynamicRouteNames.forEach((name) => router.hasRoute(name) && router.removeRoute(name))
  dynamicRouteNames = []
  const existingPaths = new Set(router.getRoutes().map((route) => route.path))
  for (const route of menuToRoutes(nodes)) {
    const fullPath = `/admin/${String(route.path).replace(/^\/+/, '')}`
    const duplicatedByName = typeof route.name === 'string' && router.hasRoute(route.name)
    if (duplicatedByName || existingPaths.has(fullPath)) {
      continue
    }
    router.addRoute('admin', route)
    if (typeof route.name === 'string') {
      dynamicRouteNames.push(route.name)
    }
  }
}

/**
 * 菜单节点的路由 index。
 *
 * 菜单 `path` 是相对 `/admin` 的完整路由（不是相对父级的片段），并允许携带 `{clientId}` 占位符
 * —— 例如 RBAC 的真实路由是 `/admin/rbac/apps/:clientId/roles`，菜单只存 `rbac/apps/{clientId}/roles`，
 * 渲染时才按当前选中应用替换（附录C §2：菜单结构与应用解耦）。
 *
 * 占位符是 `clientId` 而不是 `appId`：URL 里出现的是不可枚举的业务标识，
 * 换成自增主键就等于让使用者"把数字加一再回车"去挨个试应用。
 */
function menuIndex(node: MenuNode, clientId?: string | null): string {
  return `/admin/${node.path.replace('{clientId}', clientId ?? '').replace(/^\/+/, '')}`
}

/** 菜单树的所有路径（含子级），供路由守卫做「越权兜底」（不在菜单树中的路径按 403 处理）。 */
export function flattenMenuPaths(nodes: MenuNode[], clientId?: string | null): string[] {
  return nodes.flatMap((node) => {
    const full = menuIndex(node, clientId)
    return node.children?.length ? [full, ...flattenMenuPaths(node.children, clientId)] : [full]
  })
}

/** 菜单树 → el-menu 的层级数据（AdminLayout 渲染用）。 */
export interface MenuItem {
  index: string
  title: string
  icon?: string | null
  children?: MenuItem[]
}

export function toMenuItems(nodes: MenuNode[], clientId?: string | null): MenuItem[] {
  return [...nodes]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((node) => {
      const index = menuIndex(node, clientId)
      const children = node.children?.length ? toMenuItems(node.children, clientId) : undefined
      return { index, title: node.title, icon: node.icon ?? null, ...(children ? { children } : {}) }
    })
}
